import {useEffect, useState} from 'react';

import {useLatest} from './useLatest';

export interface VisibilityQueueItem {
    localId: string;
    visible: boolean;
}

interface VisibilityQueueProps {
    areas: Record<string, Area>;
    tracks: Record<string, Track>;
    waypoints: Record<string, Waypoint>;
    folders: Record<string, Folder>;
    setVisibilities: SetState<Record<string, boolean>>;
}

export default function useParentFolderVisibility({
    areas,
    tracks,
    waypoints,
    folders,
    setVisibilities,
}: VisibilityQueueProps) {
    const [visibilityQueue, setVisibilityQueue] = useState<VisibilityQueueItem[]>([]);

    const latestVisibilityQueue = useLatest(visibilityQueue);

    useEffect(() => {
        if (latestVisibilityQueue.current.length === 0) return;

        let nextVisibilities = {};
        const queue: VisibilityQueueItem[] = [...latestVisibilityQueue.current];
        const nextQueue: VisibilityQueueItem[] = [];

        while (queue.length > 0) {
            const item: VisibilityQueueItem = queue.pop() as VisibilityQueueItem;
            const {localId, visible} = item;
            let id;
            // We need to store the server id in visibilities so that the item's
            // visibility state persists across reloads. When an item is first created,
            // its localId is a temporary value that only exists for the current session.
            if (areas[localId]) {
                id = areas[localId].properties.id;
            } else if (tracks[localId]) {
                id = tracks[localId].properties.id;
            } else if (waypoints[localId]) {
                id = waypoints[localId].properties.id;
            } else if (folders[localId]) {
                id = folders[localId].id;
            }
            if (id) {
                nextVisibilities = {
                    ...nextVisibilities,
                    [id]: visible,
                };
            } else {
                // The id may not be immediately available depending on server response time
                // after saving. Put it back in the queue for a retry.
                nextQueue.push(item);
            }
        }
        setVisibilityQueue(nextQueue);
        setVisibilities((lastVisibilities) => ({
            ...lastVisibilities,
            ...nextVisibilities,
        }));
    }, [latestVisibilityQueue, areas, tracks, waypoints, folders, setVisibilities]);

    return [setVisibilityQueue];
}
